import {
  createContext,
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useLocation } from 'react-router';

import { ArticleDisplayedStep, ArticleTaskType } from '@/api';
import { FilterDateValue } from '@/design_system/Filters/FiltersMenu/FilterDate';
import { DateRangeValue } from '@/design_system/InputDate/InputDateRange';
import { ArticleServiceType, ArticleWarrantyType } from '@/models/article';
import { Brand } from '@/models/brand';
import { RequestorType } from '@/models/request';
import { useCurrentSession } from '@/services/auth';
import { useLocalStorageState } from '@/services/localStorage';
import { useDebouncedSearchParamState } from '@/utils/useDebouncedState';
import { useSyncSearchParams } from '@/utils/useSearchParams';

import { SortConfig, SubLocationFilterOption } from './utils';

export type Tab = 'to-do' | 'waiting' | 'all';

export const DEFAULT_TAB_SORT = {
  'to-do': {
    field: 'statusDueAt',
    order: 'asc',
  },
  waiting: {
    field: 'statusDueAt',
    order: 'asc',
  },
  all: {
    field: 'referencedAt',
    order: 'asc',
  },
} satisfies SortConfig;

type FiltersState = {
  tab: {
    tab: Tab;
    setTab: (tab: Tab) => void;
  };
  page: {
    page: number;
    setPage: (page: number) => void;
  };
  search: {
    search: string;
    debouncedSearch: string;
    debouncedSetSearch: (search: string) => void;
  };
  requestorTypes: {
    requestorTypes: RequestorType[];
    setRequestorTypes: (types: RequestorType[]) => void;
  };
  displayedSteps: {
    displayedSteps: ArticleDisplayedStep[];
    setDisplayedSteps: (steps: ArticleDisplayedStep[]) => void;
  };
  tasks: {
    tasks: ArticleTaskType[];
    setTasks: (tasks: ArticleTaskType[]) => void;
  };
  serviceTypes: {
    serviceTypes: ArticleServiceType[];
    setServiceTypes: (serviceTypes: ArticleServiceType[]) => void;
  };
  warrantyTypes: {
    warrantyTypes: ArticleWarrantyType[];
    setWarrantyTypes: (warrantyTypes: ArticleWarrantyType[]) => void;
  };
  collaborators: {
    collaborators: { id: string; name: string }[];
    setCollaborators: (collaborators: { id: string; name: string }[]) => void;
  };
  brands: {
    brands: Brand[];
    setBrands: (brands: Brand[]) => void;
  };
  stores: {
    stores: { id: string; name: string }[];
    setStores: (stores: { id: string; name: string }[]) => void;
  };
  workshops: {
    workshops: { id: string; name: string }[];
    setWorkshops: (workshops: { id: string; name: string }[]) => void;
  };
  stockLocations: {
    stockLocations: { id: string; name: string }[];
    setStockLocations: (stockLocations: { id: string; name: string }[]) => void;
  };
  supervisors: {
    supervisors: { id: string; name: string }[];
    setSupervisors: (supervisors: { id: string; name: string }[]) => void;
  };
  createdAt: {
    createdAt: { id: FilterDateValue; start: string | null; end: string | null };
    setCreatedAt: (createdAt: {
      id: FilterDateValue;
      start: string | null;
      end: string | null;
    }) => void;
  };
  subLocations: {
    subLocations: SubLocationFilterOption[];
    setSubLocations: (newSubLocations: SubLocationFilterOption[]) => void;
  };
  activeFiltersCount: number;
  clearFilters: () => void;
  sort: {
    sort: SortConfig;
    setSort: Dispatch<SetStateAction<SortConfig>>;
  };
};

export const FiltersStateContext = createContext<FiltersState>({} as FiltersState);

export const useFiltersState = (): FiltersState => {
  const [tab, setTab] = useLocalStorageState<Tab>('requests:filters:tab', 'to-do');

  const [page, setPage] = useState(1);
  const resetPage = useCallback(() => setPage(1), [setPage]);

  const [search, debouncedSearch, debouncedSetSearch, syncSearch] =
    useDebouncedSearchParamState<string>('requests', 'search', '', 500, resetPage);

  const [requestorTypes, setRequestorTypes] = useLocalStorageState<RequestorType[]>(
    'requests:filters:requestor-types',
    []
  );

  const [displayedSteps, setDisplayedSteps] = useLocalStorageState<ArticleDisplayedStep[]>(
    'requests:filters:displayed-steps',
    []
  );

  const [tasks, setTasks] = useLocalStorageState<ArticleTaskType[]>('requests:filters:tasks', []);

  const [serviceTypes, setServiceTypes] = useLocalStorageState<ArticleServiceType[]>(
    'requests:filters:serviceTypes',
    []
  );

  const [warrantyTypes, setWarrantyTypes] = useLocalStorageState<ArticleWarrantyType[]>(
    'requests:filters:warrantyTypes',
    []
  );

  const [stores, setStores] = useLocalStorageState<{ id: string; name: string }[]>(
    'requests:filters:stores',
    []
  );

  const [workshops, setWorkshops] = useLocalStorageState<{ id: string; name: string }[]>(
    'requests:filters:workshops',
    []
  );

  const [stockLocations, setStockLocations] = useLocalStorageState<{ id: string; name: string }[]>(
    'requests:filters:stockLocations',
    []
  );

  const [collaborators, setCollaborators] = useLocalStorageState<{ id: string; name: string }[]>(
    'requests:filters:collaborators',
    []
  );

  const [brands, setBrands] = useLocalStorageState<Brand[]>('requests:filters:brands', []);

  const [supervisors, setSupervisors] = useLocalStorageState<{ id: string; name: string }[]>(
    'requests:filters:supervisors',
    []
  );

  const [subLocations, setSubLocations] = useLocalStorageState<SubLocationFilterOption[]>(
    'requests:filters:subLocations',
    []
  );

  const [sort, setSort] = useLocalStorageState<SortConfig>(
    'requests:sort-by-tab',
    DEFAULT_TAB_SORT
  );

  const [createdAt, setCreatedAt] = useLocalStorageState<{
    id: FilterDateValue;
    start: DateRangeValue['start'] | null;
    end: DateRangeValue['end'] | null;
  }>('requests:filters:createdAt', { id: 'all', start: null, end: null });

  const clearFilters = () => {
    setRequestorTypes([]);
    setDisplayedSteps([]);
    setTasks([]);
    setServiceTypes([]);
    setWarrantyTypes([]);
    setStores([]);
    setWorkshops([]);
    setStockLocations([]);
    setCollaborators([]);
    setBrands([]);
    setSupervisors([]);
    setCreatedAt({ id: 'all', start: null, end: null });
    setSubLocations([]);
    resetPage();
  };

  // Deep link used by the "assigned to me" notifications and emails. The param is consumed here
  // and then dropped from the URL when the search params are synced.
  const { currentSession } = useCurrentSession();
  const { search: locationSearch } = useLocation();
  const isAssignedToMeLink = new URLSearchParams(locationSearch).get('assignedToMe') === 'true';

  useEffect(() => {
    if (!isAssignedToMeLink || !currentSession) {
      return;
    }

    clearFilters();
    debouncedSetSearch('');
    setTab('all');
    setSupervisors([{ id: currentSession.id, name: currentSession.name }]);
  }, [isAssignedToMeLink, currentSession]);

  useSyncSearchParams([syncSearch]);

  return {
    tab: {
      tab,
      setTab: (newTab) => {
        setTab(newTab);
        resetPage();
      },
    },
    page: {
      page,
      setPage,
    },
    search: {
      search,
      debouncedSearch,
      debouncedSetSearch,
    },
    requestorTypes: {
      requestorTypes,
      setRequestorTypes: (types) => {
        setRequestorTypes(types);
        resetPage();
      },
    },
    displayedSteps: {
      displayedSteps,
      setDisplayedSteps: (steps) => {
        setDisplayedSteps(steps);
        resetPage();
      },
    },
    tasks: {
      tasks,
      setTasks: (newTasks) => {
        setTasks(newTasks);
        resetPage();
      },
    },
    serviceTypes: {
      serviceTypes,
      setServiceTypes: (newServiceTypes) => {
        setServiceTypes(newServiceTypes);
        resetPage();
      },
    },
    warrantyTypes: {
      warrantyTypes,
      setWarrantyTypes: (newWarrantyTypes) => {
        setWarrantyTypes(newWarrantyTypes);
        resetPage();
      },
    },
    stores: {
      stores,
      setStores: (newStores) => {
        setStores(newStores);
        resetPage();
      },
    },
    workshops: {
      workshops,
      setWorkshops: (newWorkshops) => {
        setWorkshops(newWorkshops);
        resetPage();
      },
    },
    stockLocations: {
      stockLocations,
      setStockLocations: (newStockLocations) => {
        setStockLocations(newStockLocations);
        resetPage();
      },
    },
    collaborators: {
      collaborators,
      setCollaborators: (newCollaborators) => {
        setCollaborators(newCollaborators);
        resetPage();
      },
    },
    brands: {
      brands,
      setBrands: (newBrands) => {
        setBrands(newBrands);
        resetPage();
      },
    },
    supervisors: {
      supervisors,
      setSupervisors: (newSupervisors) => {
        setSupervisors(newSupervisors);
        resetPage();
      },
    },
    createdAt: {
      createdAt,
      setCreatedAt: (newCreatedAt) => {
        setCreatedAt(newCreatedAt);
        resetPage();
      },
    },
    subLocations: {
      subLocations,
      setSubLocations: (newSubLocations) => {
        setSubLocations(newSubLocations);
        resetPage();
      },
    },
    sort: {
      sort,
      setSort: (newSort) => {
        setSort(newSort);
        resetPage();
      },
    },
    activeFiltersCount:
      requestorTypes.length +
      displayedSteps.length +
      tasks.length +
      serviceTypes.length +
      warrantyTypes.length +
      collaborators.length +
      brands.length +
      stores.length +
      workshops.length +
      stockLocations.length +
      supervisors.length +
      subLocations.length +
      (createdAt.id !== 'all' ? 1 : 0),
    clearFilters,
  };
};

export const useFilters = () => {
  return useContext(FiltersStateContext);
};
